import React, { forwardRef } from "react";
import { BRAND } from "@/lib/api";
import { User } from "lucide-react";

function formatBirth(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}

function formatCPF(cpf) {
  if (!cpf) return null;
  const digits = String(cpf).replace(/\D/g, "");
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Carteirinha do atleta.
 * Mostra: Foto, Nome, Categoria, Posição, Nascimento, CPF, Responsável (+ telefone).
 */
export const Carteirinha = forwardRef(function Carteirinha({ student }, ref) {
  if (!student) return null;
  const responsavel = [student.guardian, student.guardian_phone].filter(Boolean).join(" · ") || "—";
  return (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-2xl border border-[hsl(var(--vv-lime))/0.4] shadow-2xl"
      style={{
        width: 300,
        minHeight: 525,
        background: "linear-gradient(160deg, hsl(218 70% 10%) 0%, hsl(218 55% 15%) 55%, hsl(218 65% 8%) 100%)",
        color: "hsl(48 100% 96%)",
        fontFamily: "'Barlow Condensed', sans-serif",
      }}
      data-testid="carteirinha-card"
    >
      <div
        className="absolute inset-0 opacity-70 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 110% -10%, hsl(var(--vv-lime)/0.35), transparent 45%), radial-gradient(circle at -10% 110%, hsl(var(--vv-blue)/0.55), transparent 55%)",
        }}
      />
      <div
        className="h-1.5 w-full"
        style={{
          background:
            "repeating-linear-gradient(90deg, hsl(var(--vv-blue)) 0 12px, hsl(var(--vv-yellow)) 12px 24px, hsl(var(--vv-lime)) 24px 36px)",
        }}
      />

      <div className="relative px-4 pt-3 pb-2 flex items-center gap-2">
        <img src={BRAND.LOGO} alt="" className="h-14 w-auto" style={{ filter: "drop-shadow(0 0 8px hsl(82 90% 55% / 0.6))" }} />
        <div>
          <div className="text-[8px] uppercase tracking-[0.25em]" style={{ color: "hsl(82 90% 60%)" }}>CARTEIRA DE ATLETA</div>
          <div className="text-base font-black leading-none" style={{ fontFamily: "'Archivo Black', sans-serif" }}>VÔLEI VIDA</div>
          <div className="text-[9px] text-white/70">Jardinópolis · 2023</div>
        </div>
      </div>

      <div className="relative px-4">
        <div
          className="aspect-square w-full rounded-lg overflow-hidden border-[3px]"
          style={{
            borderColor: "hsl(var(--vv-lime))",
            boxShadow: "0 0 0 2px hsl(var(--vv-blue)), 0 8px 22px -6px rgba(0,0,0,0.5)",
          }}
        >
          {student.photo_url ? (
            <img src={student.photo_url} alt={student.name} className="w-full h-full object-cover" crossOrigin="anonymous" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: "hsl(218 50% 18%)" }}>
              <User className="size-16 opacity-60" />
            </div>
          )}
        </div>
      </div>

      <div className="relative px-4 pt-3 pb-5">
        <div className="text-[9px] uppercase tracking-[0.25em]" style={{ color: "hsl(48 100% 60%)" }}>Atleta</div>
        <div className="text-[21px] font-black leading-tight" style={{ fontFamily: "'Archivo Black', sans-serif" }}>
          {student.name}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Badge label="Categoria" value={student.category || "—"} color="blue" />
          <Badge label="Posição" value={student.position || "—"} color="lime" />
          <Badge label="Nascimento" value={formatBirth(student.birth_date)} color="lime" />
          <Badge label="CPF" value={formatCPF(student.cpf) || "—"} color="blue" />
          <div className="col-span-2">
            <Badge label="Responsável" value={responsavel} color="blue" />
          </div>
        </div>

        <div className="mt-3 pt-2 flex items-center justify-between text-[9px] uppercase tracking-wider border-t border-white/10">
          <span className="text-white/60">ID: {student.id?.slice(0, 8).toUpperCase()}</span>
          <span style={{ color: "hsl(82 90% 60%)" }}>#{(student.id?.slice(-4) || "0000").toUpperCase()}</span>
        </div>
      </div>

      <div
        className="h-1.5 w-full absolute bottom-0 inset-x-0"
        style={{
          background:
            "repeating-linear-gradient(90deg, hsl(var(--vv-lime)) 0 12px, hsl(var(--vv-yellow)) 12px 24px, hsl(var(--vv-blue)) 24px 36px)",
        }}
      />
    </div>
  );
});

function Badge({ label, value, color = "blue" }) {
  const palette = color === "lime"
    ? { border: "hsl(82 90% 55%)", glow: "hsl(82 90% 55% / 0.25)", labelColor: "hsl(82 90% 70%)" }
    : { border: "hsl(213 94% 60%)", glow: "hsl(213 94% 55% / 0.25)", labelColor: "hsl(213 94% 75%)" };
  return (
    <div
      className="rounded-lg px-2.5 py-1.5"
      style={{
        background: "hsl(218 45% 12% / 0.85)",
        border: `2px solid ${palette.border}`,
        boxShadow: `0 0 12px -4px ${palette.glow}, inset 0 0 0 1px hsl(0 0% 100% / 0.04)`,
      }}
    >
      <div className="text-[8px] uppercase tracking-[0.18em] font-bold leading-none mb-0.5" style={{ color: palette.labelColor }}>
        {label}
      </div>
      <div
        className="font-bold leading-tight"
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: "16px",
          letterSpacing: "0.01em",
          color: "hsl(48 100% 92%)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default Carteirinha;
